import React, { ReactElement, CSSProperties, useMemo } from 'react';
import { ItemCallback, Layout as ReactGridLayout } from 'react-grid-layout';
import styled from 'styled-components';
import Widget, { WidgetType } from 'domain/entity/widget';
import { LayoutType, LayoutElementType, ConstraintsPerLayoutElement, isAnalysisLayoutElement } from 'domain/entity/layout';
import WidgetContainer from 'adapter/in/common/containers/Widget';
import ReactGrid, { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import i18n from 'asset/translation';
import { GridOrigin, elementFitsContraints } from 'adapter/in/dashboards/containers/Grid';
import { WidgetBeingResized } from 'adapter/in/dashboards/containers/Grid/useGridView';
import Dashboard from 'domain/entity/dashboard';
import { Option } from 'fp-ts/lib/Option';
import { Loading } from '@dexma/ui-components';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { FrameWidgetData } from 'adapter/in/reports/components/CommonReportGrid/CommonReportGrid';
import { useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { getOverflowSafeLayout, isA4Breakpoint } from 'adapter/in/dashboards/components/generic/ReactGrid/utils';
import { NewWidgetOverlay } from 'adapter/in/dashboards/components/Grid/NewWidgetOverlay';
import { WidgetCoordinates } from 'adapter/in/dashboards/components/Grid/newWidgetOverlayUtils';
import { A4_ROWS_PER_PAGE, CELL_MARGIN, COLUMNS } from 'adapter/in/dashboards/components/Grid/constants';
import { SaveReportTemplateLayoutUseCase } from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';
import { BusinessInsight } from 'domain/entity/feature';
import { isLocalBusinessInsightId } from 'adapter/in/dashboards/components/Grid/localBusinessInsight';

export * from 'adapter/in/dashboards/components/Grid/constants';

export type Breakpoint = LayoutType.DESKTOP | LayoutType.TABLET | LayoutType.MOBILE | LayoutType.A4;

export type GridLayoutsAndWidgetMap = { gridLayouts: GridLayouts; widgetMap: Map<string, Widget> };
type GridProps = {
    dashboardId: string;
    isEditEnabled: boolean;
    widgetMap: Map<string, Widget>;
    gridLayouts: GridLayouts;
    gridLayoutsWithFrame?: GridLayouts;
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    onWidthChange: (newWidth: number) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onDragStart: ItemCallback;
    onDragStop: ItemCallback;
    isGrabbingWidget?: boolean;
    onCoordinatesHoverChange?: (hasCoordinates: boolean) => void;
    onTextCoordinatesSelected?: (coordinates: WidgetCoordinates) => void;
    onBusinessInsightCoordinatesSelected?: (
        coordinates: WidgetCoordinates,
        businessInsight: BusinessInsight,
        businessInsightId: string
    ) => void;
    contextPeriod?: string;
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    wrongSizeElements: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>;
    updateFrameDependencies?: {
        updateFrameData: Option<Dashboard>;
        updateFrameIsLoading: boolean;
    };
    frameData?: FrameWidgetData;
    widgetBeingResized?: WidgetBeingResized;
    gridOrigin?: GridOrigin;
    isReportInstance?: boolean;
};

const WidgetContainerWrapper = styled.div<{ $widgetId: string; $isEditEnabled: boolean }>`
    .ProseMirror {
        display: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') || $widgetId.startsWith('__a4_header') ? 'flex' : '')};
        flex-direction: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') || $widgetId.startsWith('__a4_header') ? 'row' : '')};

        align-items: ${({ $widgetId }) => ($widgetId.startsWith('__a4_header') ? 'center' : '')};

        justify-content: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') ? 'space-between' : '')};

        img {
            ${({ $widgetId, $isEditEnabled }) =>
                $widgetId.startsWith('__a4_header') ? `margin-right: 10px; height: ${$isEditEnabled ? '30px' : '50px'}; width: auto;` : ''};
        }

        p {
            ${({ $widgetId }) =>
                $widgetId.startsWith('__a4_header')
                    ? 'margin-top: 0; margin-bottom: 0; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;'
                    : ''};
        }
    }
`;

const Grid = (props: GridProps): ReactElement => {
    const {
        dashboardId,
        cellSize,
        backgroundStyle,
        widgetMap,
        gridLayouts,
        gridLayoutsWithFrame,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        onResize,
        onResizeStop,
        onDragStart,
        onDragStop,
        isGrabbingWidget = false,
        onCoordinatesHoverChange,
        onTextCoordinatesSelected,
        onBusinessInsightCoordinatesSelected,
        contextPeriod,
        updateTemplateLayout,
        onWidthChange,
        onLayoutChange,
        onBreakpointChange,
        isEditEnabled,
        wrongSizeElements,
        frameData,
        updateFrameDependencies,
        widgetBeingResized,
        gridOrigin = GridOrigin.DASHBOARD,
        isReportInstance,
    } = props;

    const getWidgetType = (layoutElementType: LayoutElementType) => {
        if (layoutElementType === LayoutElementType.BUSINESS_INSIGHT) {
            return WidgetType.BUSINESS_INSIGHT;
        }

        return isAnalysisLayoutElement(layoutElementType) ? WidgetType.ANALYSIS : WidgetType.RICH_TEXT;
    };

    const { editLayoutMode } = useLayoutsView();
    const isA4 = isA4Breakpoint(currentBreakpoint);

    const getWidgetObject = (widgetLayout: GridLayoutElement) => {
        if (widgetLayout.type === LayoutElementType.WIDGET && widgetMap.get(widgetLayout.i)) {
            return widgetMap.get(widgetLayout.i) as Widget;
        }
        const widgetId =
            widgetLayout.type === LayoutElementType.BUSINESS_INSIGHT &&
            isLocalBusinessInsightId(widgetLayout.i) &&
            widgetLayout.businessInsightType
                ? widgetLayout.businessInsightType
                : widgetLayout.i;

        return {
            id: widgetId,
            name: '',
            type: getWidgetType(widgetLayout.type),
            presetKey: '',
            optionFields: [],
            configurationFields: [],
        };
    };

    const layoutToRender = useMemo(() => {
        if (isA4 && gridLayoutsWithFrame) {
            return gridLayoutsWithFrame[LayoutType.A4] ?? getCurrentLayout(gridLayouts, LayoutType.A4) ?? [];
        }

        const baseLayout = getOverflowSafeLayout(getCurrentLayout(gridLayouts, currentBreakpoint) ?? [], COLUMNS[currentBreakpoint]);
        return baseLayout.map((el) => ({
            ...el,
            static: !editLayoutMode || isDashboardFrame(el.i, el.type),
        }));
    }, [gridLayoutsWithFrame, currentBreakpoint, gridLayouts, editLayoutMode, isA4]);

    const effectiveGridLayouts = useMemo(() => {
        const source =
            isA4 && gridLayoutsWithFrame
                ? {
                      ...gridLayouts,
                      [LayoutType.A4]: gridLayoutsWithFrame[LayoutType.A4] ?? gridLayouts[LayoutType.A4],
                  }
                : gridLayouts;

        const applyStatic = (key: LayoutType, layout?: GridLayoutElement[]) =>
            getOverflowSafeLayout(layout ?? [], COLUMNS[key]).map((el) => ({
                ...el,
                static: !editLayoutMode || isDashboardFrame(el.i, el.type),
            }));

        return Object.keys(source).reduce((acc, key) => {
            acc[key as LayoutType] = applyStatic(key as LayoutType, source[key as LayoutType]);
            return acc;
        }, {} as GridLayouts);
    }, [gridLayouts, gridLayoutsWithFrame, currentBreakpoint, editLayoutMode, isA4]);

    return (
        <NewWidgetOverlay
            isEnabled={isEditEnabled && editLayoutMode && isA4 && !isGrabbingWidget}
            layout={layoutToRender}
            cellSize={cellSize}
            onCoordinatesHoverChange={onCoordinatesHoverChange}
            onTextCoordinatesSelected={onTextCoordinatesSelected}
            onBusinessInsightCoordinatesSelected={onBusinessInsightCoordinatesSelected}
            reportTemplateId={dashboardId}
            contextPeriod={contextPeriod}
            updateTemplateLayout={updateTemplateLayout}
        >
            <ReactGrid
                gridLayouts={effectiveGridLayouts}
                cellMargin={[CELL_MARGIN, CELL_MARGIN]}
                columns={COLUMNS}
                breakpoints={breakpoints}
                rowHeight={cellSize.height}
                backgroundStyle={backgroundStyle}
                containerWidth={containerWidth}
                onDragStart={onDragStart}
                onDrag={onDragStart}
                onResize={onResize}
                onResizeStop={onResizeStop}
                onDragStop={onDragStop}
                onWidthChange={onWidthChange}
                onLayoutChange={onLayoutChange}
                onBreakpointChange={(newBreakpoint: string) => onBreakpointChange(newBreakpoint as Breakpoint)}
            >
                {layoutToRender.map((widgetLayout) => {
                    const isCorrectlyBeingResized =
                        widgetBeingResized && widgetBeingResized.i === widgetLayout.i ? widgetBeingResized.fitsInGrid : true;
                    const isCorrectlySetInGrid = elementFitsContraints(widgetLayout);
                    const widget = getWidgetObject(widgetLayout);
                    if (updateFrameDependencies?.updateFrameIsLoading && isDashboardFrame(widgetLayout.i, widgetLayout.type)) {
                        return (
                            <WidgetContainerWrapper key={widgetLayout.i} $widgetId={widgetLayout.i} $isEditEnabled={isEditEnabled}>
                                <Loading isLoading size={20} />
                            </WidgetContainerWrapper>
                        );
                    }
                    const page = currentBreakpoint === LayoutType.A4 ? Math.floor(widgetLayout.y / A4_ROWS_PER_PAGE) : 0;
                    return (
                        <WidgetContainerWrapper
                            key={widgetLayout.i}
                            data-widget-id={widgetLayout.i}
                            $widgetId={widgetLayout.i}
                            $isEditEnabled={isEditEnabled}
                        >
                            <WidgetContainer
                                data-widget-page={page}
                                widgetPage={page}
                                dashboardId={dashboardId}
                                frameData={frameData}
                                widgetWidth={widgetLayout.w}
                                widgetHeight={widgetLayout.h}
                                updateFrameData={updateFrameDependencies?.updateFrameData}
                                type={widgetLayout.type}
                                widget={widget}
                                isEditingLayout={!widgetLayout.static}
                                isEditEnabled={isEditEnabled}
                                layoutType={currentBreakpoint}
                                gridOrigin={gridOrigin}
                                alertTooltip={getAlertTooltip(widgetLayout.i, wrongSizeElements, currentBreakpoint, isEditEnabled)}
                                isReportInstance={isReportInstance}
                                sizeIssue={
                                    isCorrectlySetInGrid && isCorrectlyBeingResized
                                        ? undefined
                                        : {
                                              maxHeight: widgetLayout.maxHeight,
                                              maxWidth: widgetLayout.maxWidth,
                                              minHeight: widgetLayout.minHeight,
                                              minWidth: widgetLayout.minWidth,
                                              type: currentBreakpoint,
                                          }
                                }
                            />
                        </WidgetContainerWrapper>
                    );
                })}
            </ReactGrid>
        </NewWidgetOverlay>
    );
};

const getAlertTooltip = (
    widgetId: string,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>,
    currentBreakpoint: Breakpoint,
    isEditEnabled: boolean
): string | undefined => {
    let wrongLayoutTypes: (string | undefined)[] = [];
    if (isEditEnabled && !wrongSizeWidgets[currentBreakpoint][widgetId]) {
        const wrongSizeWidgetsCopy = { ...wrongSizeWidgets };
        delete wrongSizeWidgetsCopy[currentBreakpoint];
        wrongLayoutTypes = Object.keys(wrongSizeWidgetsCopy)
            .map((layoutType) => {
                if (
                    Object.keys(wrongSizeWidgetsCopy[layoutType as LayoutType]).find(
                        (wrongSizeWidgetId: string) => wrongSizeWidgetId === widgetId
                    )
                ) {
                    return i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`);
                }
                return undefined;
            })
            .filter((layoutType) => layoutType !== undefined);
    }
    return wrongLayoutTypes.length > 0
        ? i18n.t('widgets.alertWrongSizeOnLayouts', {
              layoutTypes: wrongLayoutTypes.toString().toLowerCase().replaceAll(',', ', '),
          })
        : undefined;
};

const getCurrentLayout = (reactGridLayouts: GridLayouts, breakpoint: LayoutType) =>
    reactGridLayouts[breakpoint] ?? reactGridLayouts[LayoutType.DESKTOP];

export default Grid;
